/**
 * Render one value as text.
 *
 * An object is JSON-encoded rather than left to default stringification: "[object Object]" in
 * an export meant to be reconciled against a provider invoice is worse than useless, because
 * it looks like data.
 */
function asText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value.toString();
  }
  return JSON.stringify(value) ?? '';
}

/** Escape one CSV cell: quotes doubled, and anything with a separator or newline quoted. */
function cell(value: unknown): string {
  const text = asText(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Build a CSV from rows of plain values.
 *
 * Money columns must be passed as the raw decimal STRINGS the API returned, not as formatted
 * display text — the export exists to be reconciled against a provider invoice, and "$0.00"
 * reconciles against nothing.
 */
export function toCsv(headers: { key: string; label: string }[], rows: Record<string, unknown>[]): string {
  const lines = [headers.map((h) => cell(h.label)).join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => cell(row[h.key])).join(','));
  }
  return lines.join('\r\n');
}

/** Trigger a browser download of `content` as `filename`. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
